import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { pauseRecord, endRecord } from '../reduxStore/actions/recordActionsHelper';

/**
 * Custom hook for managing navigation guards during active examination sessions
 * @param {boolean} hasActiveSession - Whether user has an active examination session
 * @param {string} activeRecordId - ID of the active record
 */
const useNavigationGuard = (hasActiveSession, activeRecordId) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [showNavigationModal, setShowNavigationModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const navigationBlockedRef = useRef(false);

    // Intercept navigation attempts
    const interceptNavigation = useCallback((targetPath) => {

        if (hasActiveSession) {
            setPendingNavigation(targetPath);
            setShowNavigationModal(true);
            return false; // Block navigation
        }
        return true; // Allow navigation
    }, [hasActiveSession]);

    // Monitor location changes to detect navigation attempts
    const prevLocationRef = useRef(location.pathname);

    useEffect(() => {
        // If location changed and we have an active session, intercept it
        if (hasActiveSession &&
            prevLocationRef.current !== location.pathname &&
            !navigationBlockedRef.current) {

            // Set flag to prevent recursive navigation
            navigationBlockedRef.current = true;

            // Navigate back to previous location
            navigate(prevLocationRef.current, { replace: true });

            // Show confirmation modal
            setPendingNavigation(location.pathname);
            setShowNavigationModal(true);

            // Reset the flag after a short delay
            setTimeout(() => {
                navigationBlockedRef.current = false;
            }, 100);

            return;
        }

        // Update previous location reference
        prevLocationRef.current = location.pathname;
    }, [location.pathname, hasActiveSession, navigate]);

    // Handle browser back/forward/refresh and link clicks
    useEffect(() => {
        if (!hasActiveSession) return;

        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = 'You have an active examination session. Your progress will be saved if you leave.';
            return event.returnValue;
        };

        // Handle browser back/forward buttons
        const handlePopState = (event) => {

            if (hasActiveSession && !navigationBlockedRef.current) {
                // Prevent the navigation by pushing current state back
                navigationBlockedRef.current = true;
                window.history.pushState(null, '', location.pathname);

                // Show the confirmation modal
                setPendingNavigation(null); // We don't know where they were trying to go
                setShowNavigationModal(true);

                // Reset the flag
                setTimeout(() => {
                    navigationBlockedRef.current = false;
                }, 100);
            }
        };

        // Intercept link clicks
        const handleLinkClick = (event) => {
            if (hasActiveSession && navigationBlockedRef.current === false) {
                const target = event.target.closest('a');
                if (target && target.href) {
                    const url = new URL(target.href);
                    // Check if it's an internal navigation
                    if (url.origin === window.location.origin && url.pathname !== location.pathname) {
                        event.preventDefault();
                        setPendingNavigation(url.pathname);
                        setShowNavigationModal(true);
                    }
                }
            }
        };

        // Push a state to catch back button
        window.history.pushState(null, '', location.pathname);

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('popstate', handlePopState);
        document.addEventListener('click', handleLinkClick, true);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handlePopState);
            document.removeEventListener('click', handleLinkClick, true);
        };
    }, [hasActiveSession, location.pathname]);



    // Navigation decision handlers
    const handlePauseAndLeave = useCallback(() => {
        if (activeRecordId) {
            dispatch(pauseRecord.request({ recordId: activeRecordId }));
        }

        setShowNavigationModal(false);

        // Allow navigation by clearing the blocked flag and navigating
        if (pendingNavigation) {
            navigationBlockedRef.current = true;
            setTimeout(() => {
                navigate(pendingNavigation);
                navigationBlockedRef.current = false;
            }, 500);
        } else {
            // For browser back button, go to dashboard
            navigationBlockedRef.current = true;
            setTimeout(() => {
                navigate('/user-dashboard');
                navigationBlockedRef.current = false;
            }, 500);
        }

        setPendingNavigation(null);
    }, [dispatch, navigate, activeRecordId, pendingNavigation]);

    const handleEndAndLeave = useCallback(() => {
        if (activeRecordId) {
            dispatch(endRecord.request({ recordId: activeRecordId }));
        }

        setShowNavigationModal(false);

        // Allow navigation by clearing the blocked flag and navigating
        if (pendingNavigation) {
            navigationBlockedRef.current = true;
            setTimeout(() => {
                navigate(pendingNavigation);
                navigationBlockedRef.current = false;
            }, 500);
        } else {
            // For browser back button, go to dashboard
            navigationBlockedRef.current = true;
            setTimeout(() => {
                navigate('/user-dashboard');
                navigationBlockedRef.current = false;
            }, 500);
        }

        setPendingNavigation(null);
    }, [dispatch, navigate, activeRecordId, pendingNavigation]);

    const handleContinueSession = useCallback(() => {
        // Simply dismiss the modal, stay on current page
        setShowNavigationModal(false);
        setPendingNavigation(null);
    }, []);

    return {
        showNavigationModal,
        pendingNavigation,
        handlePauseAndLeave,
        handleEndAndLeave,
        handleContinueSession,
        interceptNavigation, // Keep this for backward compatibility with manual navigation
        currentPath: location.pathname
    };
};

export default useNavigationGuard;